/**
 * Summary stat tiles: spend, tokens, requests, blended cost/1M tokens.
 *
 * `rows` should be the filtered, fine-grained rows (not the grouped
 * table rows) so the active-user count reflects real distinct users.
 */

import { type ReactElement } from 'react';
import { formatCost, formatRequests, formatTokens, sumNullableCost } from './usageFormat';
import type { UsageDisplayRow } from './types';

interface UsageTilesProps {
  rows: UsageDisplayRow[];
  rangeLabel: string;
}

/**
 * Every token the upstream billed for, across all four tiers.
 *
 * Counting only `tokensIn + tokensOut` is not a rounding error on an
 * agentic client -- Claude Code puts its cache breakpoints at the end of
 * the prompt, so nearly all of a request's input arrives as a cache read
 * or a cache write and `tokensIn` is a two-token remainder. A live
 * request measured in=2 out=882 cache_read=72,291 cache_write=1,304: the
 * old sum saw 884 of 74,479 tokens, and dividing real dollars by that
 * 1% inflated "Cost / 1M tokens" by 85x. All four tiers are priced (see
 * `ai_model_prices`), so all four are tokens.
 */
function rowTokens(row: UsageDisplayRow): number {
  return row.tokensIn + row.tokensOut + row.cacheReadTokens + row.cacheWriteTokens;
}

interface Tile {
  key: string;
  label: string;
  value: string;
  sub: string;
}

export function UsageTiles({ rows, rangeLabel }: UsageTilesProps): ReactElement {
  const totalCost = sumNullableCost(rows.map((row) => row.costUsd));
  const totalTokens = rows.reduce((sum, row) => sum + rowTokens(row), 0);
  const localTokens = rows
    .filter((row) => row.costUsd === null)
    .reduce((sum, row) => sum + rowTokens(row), 0);
  const totalRequests = rows.reduce((sum, row) => sum + row.requests, 0);
  const activeUsers = new Set(rows.map((row) => row.user).filter((u): u is string => u !== null))
    .size;
  const blended =
    totalCost === null || totalTokens === 0 ? null : totalCost / (totalTokens / 1e6);

  const tiles: Tile[] = [
    { key: 'spend', label: `Spend · ${rangeLabel}`, value: formatCost(totalCost), sub: 'billable upstreams only' },
    {
      key: 'tokens',
      label: `Tokens · ${rangeLabel}`,
      value: formatTokens(totalTokens),
      sub: `${formatTokens(localTokens)} on local models`,
    },
    {
      key: 'requests',
      label: 'Requests',
      value: formatRequests(totalRequests),
      sub: `${String(activeUsers)} active ${activeUsers === 1 ? 'user' : 'users'}`,
    },
    {
      key: 'blended',
      label: 'Cost / 1M tokens',
      value: blended === null ? '—' : formatCost(blended),
      sub: 'blended, incl. free local',
    },
  ];

  return (
    <div className="usage-tiles">
      {tiles.map((tile) => (
        <div className="usage-tile" key={tile.key}>
          <span className="usage-tile__label">{tile.label}</span>
          <span className="usage-tile__value">{tile.value}</span>
          <span className="usage-tile__sub">{tile.sub}</span>
        </div>
      ))}
    </div>
  );
}
